import { useState, useEffect, useCallback, useRef } from "react";
import { useAdminApi } from "../adminUtils";
import SignatureSelect from "./SignatureSelect";
import { useStudio } from "../../../studios/StudioContext";
import "../admin-base.css";
import "../admin-layout.css";
import "../admin-controls.css";
import "../admin-misc.css";
import "../admin-responsive.css";
import "./newslettersTab.css";

// ── Constants ─────────────────────────────────────────────
// Audiences come from the server (/newsletter/lists), so each studio sees only its own
const DEFAULT_AUDIENCES = [{ value: "all", label: "All students" }];

const BLOCKS = [
  { type: "heading",   label: "Heading" },
  { type: "text",      label: "Text" },
  { type: "image",     label: "Photo" },
  { type: "imageText", label: "Photo + text" },
  { type: "button",    label: "Button" },
  { type: "divider",   label: "Divider" },
  { type: "spacer",    label: "Space" },
];
const BLOCK_LABEL = Object.fromEntries(BLOCKS.map(b => [b.type, b.label]));

const STATUS_LABEL = { draft: "Draft", submitted: "Awaiting approval", sending: "Sending…", sent: "Sent", failed: "Failed" };

const uid = () => Math.random().toString(36).slice(2, 10);

function newBlock(type) {
  const id = uid();
  switch (type) {
    case "heading":   return { id, type, text: "", level: 2, align: "left" };
    case "text":      return { id, type, html: "" };
    case "image":     return { id, type, src: "", alt: "", href: "", caption: "" };
    case "imageText": return { id, type, src: "", alt: "", href: "", heading: "", html: "", imageSide: "left", valign: "top" };
    case "button":    return { id, type, label: "Book a class", url: "", align: "center" };
    case "divider":   return { id, type };
    case "spacer":    return { id, type, size: 32 };
    default:          return null;
  }
}

// Drafts written in the first (plain-text) version → one text block
function legacyBlocks(bodyText) {
  if (!bodyText?.trim()) return [];
  const escape = s => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const html = bodyText.trim().split(/\n{2,}/).map(p => `<p>${escape(p).replace(/\n/g, "<br>")}</p>`).join("");
  return [{ id: uid(), type: "text", html }];
}

const EMPTY = {
  _id: null, subject: "", previewText: "", audience: "all",
  headerTitle: "", headerColor: "", greeting: true, signatureId: "default", blocks: [],
  status: "draft", createdBy: null, reviewNote: "",
};

function fmtDate(d) {
  return d ? new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "";
}

// Shrink photos before upload: max 1200px wide, JPEG — keeps emails light (GIFs untouched)
function resizeImage(file) {
  return new Promise((resolve, reject) => {
    if (file.type === "image/gif") return resolve(file);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale  = Math.min(1, 1200 / img.width);
      const canvas = document.createElement("canvas");
      canvas.width  = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob(b => b ? resolve(new File([b], "photo.jpg", { type: "image/jpeg" })) : reject(new Error("Could not read image")), "image/jpeg", 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
    img.src = url;
  });
}

// ── Rich text field (bold, italic, underline, link, lists) ─
function RichText({ value, onChange, placeholder }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value || "";
    // only on mount — the field is uncontrolled while typing so the cursor doesn't jump
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(ref.current.innerHTML);
  };

  const addLink = () => {
    const url = window.prompt("Link address (https://…)", "https://");
    if (!url || url === "https://") return;
    exec("createLink", /^(https?:|mailto:|tel:)/i.test(url) ? url : `https://${url}`);
  };

  const onPaste = e => {
    e.preventDefault();
    document.execCommand("insertText", false, e.clipboardData.getData("text/plain"));
    onChange(ref.current.innerHTML);
  };

  const btn = (label, title, onClick, cls = "") => (
    <button type="button" className={`nl-rt-btn ${cls}`} title={title}
      onMouseDown={e => e.preventDefault()} onClick={onClick}>{label}</button>
  );

  return (
    <div className="nl-rt">
      <div className="nl-rt-toolbar">
        {btn("B", "Bold", () => exec("bold"), "nl-rt-b")}
        {btn("I", "Italic", () => exec("italic"), "nl-rt-i")}
        {btn("U", "Underline", () => exec("underline"), "nl-rt-u")}
        {btn("Link", "Add link", addLink)}
        {btn("Unlink", "Remove link", () => exec("unlink"))}
        {btn("• List", "Bulleted list", () => exec("insertUnorderedList"))}
        {btn("1. List", "Numbered list", () => exec("insertOrderedList"))}
      </div>
      <div ref={ref} className="nl-rt-area adm-input" contentEditable suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={e => onChange(e.currentTarget.innerHTML)}
        onPaste={onPaste} />
    </div>
  );
}

// ── Photo picker ──────────────────────────────────────────
function PhotoField({ block, onChange, upload }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);
  const input = useRef(null);

  const pick = async e => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      const url = await upload(await resizeImage(file));
      onChange({ src: url, alt: block.alt || file.name.replace(/\.[^.]+$/, "") });
    } catch (ex) { setErr(ex.message || "Upload failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="nl-photo">
      <div className="nl-photo-thumb">
        {block.src ? <img src={block.src} alt="" /> : <span>No photo</span>}
      </div>
      <div className="nl-photo-controls">
        <input ref={input} type="file" accept="image/jpeg,image/png,image/gif" hidden onChange={pick} />
        <button type="button" className="adm-btn-ghost-sm" disabled={busy} onClick={() => input.current?.click()}>
          {busy ? "Uploading…" : block.src ? "Replace photo" : "Upload photo"}
        </button>
        {err && <p className="adm-error">{err}</p>}
        <label className="adm-form-label">Description (for screen readers)
          <input className="adm-input" value={block.alt} onChange={e => onChange({ alt: e.target.value })} />
        </label>
        <label className="adm-form-label">Link photo to (optional)
          <input className="adm-input" value={block.href} placeholder="https://"
            onChange={e => onChange({ href: e.target.value })} />
        </label>
      </div>
    </div>
  );
}

function Segmented({ value, options, onChange }) {
  return (
    <div className="nl-seg">
      {options.map(o => (
        <button key={o.value} type="button"
          className={`nl-seg-btn ${value === o.value ? "active" : ""}`}
          onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

// ── One block's fields ────────────────────────────────────
function BlockFields({ block, set, upload }) {
  switch (block.type) {
    case "heading":
      return (
        <>
          <input className="adm-input" value={block.text} placeholder="Heading"
            onChange={e => set({ text: e.target.value })} />
          <div className="nl-row">
            <Segmented value={block.level} onChange={v => set({ level: v })}
              options={[{ value: 1, label: "Large" }, { value: 2, label: "Medium" }]} />
            <Segmented value={block.align} onChange={v => set({ align: v })}
              options={[{ value: "left", label: "Left" }, { value: "center", label: "Centre" }]} />
          </div>
        </>
      );
    case "text":
      return <RichText value={block.html} onChange={html => set({ html })} placeholder="Write here…" />;
    case "image":
      return (
        <>
          <PhotoField block={block} onChange={set} upload={upload} />
          <label className="adm-form-label">Caption (optional)
            <input className="adm-input" value={block.caption} onChange={e => set({ caption: e.target.value })} />
          </label>
        </>
      );
    case "imageText":
      return (
        <>
          <PhotoField block={block} onChange={set} upload={upload} />
          <div className="nl-row">
            <Segmented value={block.imageSide} onChange={v => set({ imageSide: v })}
              options={[{ value: "left", label: "Photo left" }, { value: "right", label: "Photo right" }, { value: "top", label: "Photo above" }]} />
            {block.imageSide !== "top" && (
              <Segmented value={block.valign} onChange={v => set({ valign: v })}
                options={[{ value: "top", label: "Text at top" }, { value: "middle", label: "Text centred" }]} />
            )}
          </div>
          <input className="adm-input" value={block.heading} placeholder="Heading (optional)"
            onChange={e => set({ heading: e.target.value })} />
          <RichText value={block.html} onChange={html => set({ html })} placeholder="Text with the photo…" />
        </>
      );
    case "button":
      return (
        <>
          <div className="nl-row">
            <input className="adm-input" value={block.label} placeholder="Button text"
              onChange={e => set({ label: e.target.value })} />
            <input className="adm-input" value={block.url} placeholder="https://"
              onChange={e => set({ url: e.target.value })} />
          </div>
          <Segmented value={block.align} onChange={v => set({ align: v })}
            options={[{ value: "left", label: "Left" }, { value: "center", label: "Centre" }]} />
        </>
      );
    case "spacer":
      return (
        <Segmented value={block.size} onChange={v => set({ size: v })}
          options={[{ value: 16, label: "Small" }, { value: 32, label: "Medium" }, { value: 48, label: "Large" }]} />
      );
    default:
      return null;
  }
}

// ═══ Tab ═══════════════════════════════════════════════════
// Owners: write, send, and approve teachers' newsletters.
// Teachers: write their own, test to their own email, submit for approval.
export default function NewslettersTab() {
  const { API, AUTH, ADMIN_SECRET, IS_OWNER } = useAdminApi();
  const { studioId } = useStudio();

  const [list, setList]       = useState([]);
  const [lists, setLists]     = useState(null);   // Brevo list counts
  const [audiences, setAudiences]       = useState(DEFAULT_AUDIENCES);
  const [defaultColor, setDefaultColor] = useState("#7b2d55");
  const AUDIENCE_LABEL = Object.fromEntries(audiences.map(a => [a.value, a.label]));
  const [syncInfo, setSync]   = useState(null);
  const [form, setForm]       = useState(null);   // null = list view
  const [dirty, setDirty]     = useState(false);
  const [previewHtml, setPH]  = useState("");
  const [device, setDevice]   = useState("desktop");
  const [testTo, setTestTo]   = useState("");
  const [busy, setBusy]       = useState(null);
  const [error, setError]     = useState(null);
  const [notice, setNotice]   = useState(null);

  // ── Data ───────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/newsletter`, { headers: AUTH });
      const d = await r.json();
      setList(d.newsletters ?? []);
    } catch { /* keep */ }
  }, [API, AUTH]);

  const loadLists = useCallback(async () => {
    try {
      const r = await fetch(`${API}/newsletter/lists`, { headers: AUTH });
      const d = await r.json();
      if (d.audiences) setAudiences(d.audiences);
      if (d.defaultColor) setDefaultColor(d.defaultColor);
      if (r.ok) { setLists(d.lists); setSync(d.lastSync); }
      else setError(`Brevo: ${d.error}`);
    } catch { /* ignore */ }
  }, [API, AUTH]);

  const sync = useCallback(async (quiet = false) => {
    if (!quiet) setBusy("sync");
    try {
      const r = await fetch(`${API}/newsletter/sync`, { method: "POST", headers: AUTH });
      const d = await r.json();
      if (!r.ok) { if (!quiet) setError(`Brevo: ${d.error}`); return; }
      setSync({ at: d.at, counts: d.counts });
      if (!quiet) setNotice("Contacts sent to Brevo. Counts update once Brevo finishes importing (usually under a minute).");
      setTimeout(loadLists, 15000);
    } catch { if (!quiet) setError("Sync failed."); }
    finally { if (!quiet) setBusy(null); }
  }, [API, AUTH, loadLists]);

  useEffect(() => {
    load();
    loadLists();
    if (IS_OWNER) sync(true);
  }, [load, loadLists, sync, IS_OWNER]);

  // ── Live preview ───────────────────────────────────────
  useEffect(() => {
    if (!form) return;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/newsletter/render`, { method: "POST", headers: AUTH, body: JSON.stringify(form) });
        const d = await r.json();
        if (r.ok) setPH(d.html);
      } catch { /* keep last preview */ }
    }, 900);
    return () => clearTimeout(t);
  }, [form, API, AUTH]);

  // ── Editing helpers ────────────────────────────────────
  const edit = patch => { setForm(f => ({ ...f, ...patch })); setDirty(true); };
  const setBlock = (id, patch) => edit({ blocks: form.blocks.map(b => b.id === id ? { ...b, ...patch } : b) });
  const addBlock = type => edit({ blocks: [...form.blocks, newBlock(type)] });
  const removeBlock = id => edit({ blocks: form.blocks.filter(b => b.id !== id) });
  const moveBlock = (id, dir) => {
    const i = form.blocks.findIndex(b => b.id === id);
    const j = i + dir;
    if (j < 0 || j >= form.blocks.length) return;
    const next = [...form.blocks];
    [next[i], next[j]] = [next[j], next[i]];
    edit({ blocks: next });
  };

  const upload = async file => {
    const fd = new FormData();
    fd.append("image", file);
    const r = await fetch(`${API}/newsletter/images`, {
      method: "POST", headers: { Authorization: AUTH.Authorization || `Bearer ${ADMIN_SECRET}` }, body: fd,
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || "Upload failed.");
    return d.url;
  };

  const clearMsgs = () => { setError(null); setNotice(null); };

  const open = n => {
    clearMsgs();
    setPH("");
    setForm(n ? {
      _id: n._id, subject: n.subject, previewText: n.previewText || "", audience: n.audience || "all",
      headerTitle: n.headerTitle || "", headerColor: n.headerColor || "", greeting: n.greeting !== false,
      signatureId: n.signatureId || "default",
      blocks: n.blocks?.length ? n.blocks.map(b => ({ ...b, id: b.id || uid() })) : legacyBlocks(n.bodyText),
      status: n.status || "draft", createdBy: n.createdBy || null, reviewNote: n.reviewNote || "",
      brand: n.brand || studioId,
    } : { ...EMPTY, brand: studioId, signatureId: IS_OWNER ? "default" : "me", blocks: [newBlock("heading"), newBlock("text")] });
    setDirty(false);
  };

  const close = () => {
    if (dirty && !window.confirm("Leave without saving your changes?")) return;
    setForm(null); clearMsgs(); load();
  };

  // ── Save / test / submit / send ────────────────────────
  const save = async () => {
    clearMsgs();
    if (!form.subject.trim()) { setError("Subject is required."); return null; }
    setBusy("save");
    try {
      const r = await fetch(form._id ? `${API}/newsletter/${form._id}` : `${API}/newsletter`, {
        method: form._id ? "PUT" : "POST", headers: AUTH, body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error ?? "Could not save."); return null; }
      setForm(f => ({ ...f, _id: d.newsletter._id }));
      setDirty(false);
      return d.newsletter._id;
    } catch { setError("Something went wrong."); return null; }
    finally { setBusy(null); }
  };

  const handleSave = async () => { if (await save()) setNotice("Draft saved."); };

  const handleTest = async () => {
    if (IS_OWNER && !testTo.includes("@")) { setError("Enter an email address for the test."); return; }
    const id = await save();
    if (!id) return;
    setBusy("test");
    try {
      const r = await fetch(`${API}/newsletter/${id}/test`, {
        method: "POST", headers: AUTH, body: JSON.stringify(IS_OWNER ? { to: testTo.trim() } : {}),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error ?? "Test send failed."); return; }
      setNotice(`Test sent to ${d.to}. It can take a minute to arrive.`);
    } catch { setError("Something went wrong."); }
    finally { setBusy(null); }
  };

  const recipientCount = lists?.[form?.audience]?.subscribers;

  const handleSend = async () => {
    const id = await save();
    if (!id) return;
    const who = `${recipientCount ?? "all"} ${AUDIENCE_LABEL[form.audience]}`;
    if (!window.confirm(`Send "${form.subject}" to ${who} now? This cannot be undone.`)) return;
    setBusy("send");
    try {
      const r = await fetch(`${API}/newsletter/${id}/send`, { method: "POST", headers: AUTH });
      const d = await r.json();
      if (!r.ok) { setError(d.error ?? "Send failed."); return; }
      setForm(null);
      setNotice(`"${form.subject}" is on its way. Opens and clicks will show here as they come in.`);
      load();
    } catch { setError("Something went wrong."); }
    finally { setBusy(null); }
  };

  // Teacher: draft → submitted
  const handleSubmit = async () => {
    const id = await save();
    if (!id) return;
    if (!window.confirm(`Submit "${form.subject}" for approval? You can't edit it while it's waiting, unless you withdraw it.`)) return;
    setBusy("submit");
    try {
      const r = await fetch(`${API}/newsletter/${id}/submit`, { method: "POST", headers: AUTH });
      const d = await r.json();
      if (!r.ok) { setError(d.error ?? "Could not submit."); return; }
      setForm(null);
      setNotice(`"${form.subject}" submitted. The studio owner will review and send it.`);
      load();
    } catch { setError("Something went wrong."); }
    finally { setBusy(null); }
  };

  // Owner: submitted → draft, with an optional note for the teacher
  const handleReturn = async () => {
    const note = window.prompt(`Note for ${form.createdBy?.name || "the teacher"} (optional):`, "");
    if (note === null) return;
    if (dirty && !(await save())) return;
    setBusy("return");
    try {
      const r = await fetch(`${API}/newsletter/${form._id}/return`, { method: "POST", headers: AUTH, body: JSON.stringify({ note }) });
      const d = await r.json();
      if (!r.ok) { setError(d.error ?? "Could not return."); return; }
      setForm(null);
      setNotice(`"${form.subject}" returned to ${form.createdBy?.name || "the teacher"}.`);
      load();
    } catch { setError("Something went wrong."); }
    finally { setBusy(null); }
  };

  // Teacher: pull a submission back to draft
  const withdraw = async n => {
    if (!window.confirm(`Withdraw "${n.subject}" so you can edit it?`)) return;
    clearMsgs();
    const r = await fetch(`${API}/newsletter/${n._id}/return`, { method: "POST", headers: AUTH });
    const d = await r.json();
    if (r.ok) load(); else setError(d.error ?? "Could not withdraw.");
  };

  const duplicate = async n => {
    clearMsgs();
    const r = await fetch(`${API}/newsletter/${n._id}/duplicate`, { method: "POST", headers: AUTH });
    const d = await r.json();
    if (r.ok) { load(); open(d.newsletter); } else setError(d.error ?? "Could not duplicate.");
  };

  const remove = async n => {
    if (!window.confirm(`Delete "${n.subject}"?`)) return;
    const r = await fetch(`${API}/newsletter/${n._id}`, { method: "DELETE", headers: AUTH });
    if (r.ok) load(); else { const d = await r.json(); setError(d.error ?? "Could not delete."); }
  };

  const refreshStats = async n => {
    const r = await fetch(`${API}/newsletter/${n._id}/stats`, { headers: AUTH });
    const d = await r.json();
    if (r.ok) setList(l => l.map(x => x._id === n._id ? { ...x, stats: d.stats, statsAt: new Date().toISOString() } : x));
    else setError(d.error ?? "Could not load stats.");
  };

  const byTeacher = n => IS_OWNER && n.createdBy?.role === "teacher" && n.createdBy?.name;

  const listsLine = lists && (
    <p className="nl-lists-line">
      Brevo lists: {audiences.map(a => `${a.label} ${lists[a.value]?.subscribers ?? 0}`).join(" · ")}
      {syncInfo?.at && <> · synced {fmtDate(syncInfo.at)}</>}
      {IS_OWNER && (
        <button type="button" className="adm-btn-ghost-sm" disabled={busy === "sync"} onClick={() => sync(false)}>
          {busy === "sync" ? "Syncing…" : "Sync contacts"}
        </button>
      )}
    </p>
  );

  // ═══ List view ═════════════════════════════════════════
  if (!form) {
    const waiting = IS_OWNER ? list.filter(n => n.status === "submitted").length : 0;
    return (
      <div className="adm-tab-content nl-tab">
        <div className="nl-head">
          <h2 className="nl-title">{IS_OWNER ? "Newsletters" : "My newsletters"}</h2>
          <button className="adm-btn-primary" onClick={() => open(null)}>+ New newsletter</button>
        </div>
        {listsLine}
        {waiting > 0 && <p className="nl-waiting">{waiting} newsletter{waiting > 1 ? "s" : ""} waiting for your approval.</p>}
        {error  && <p className="adm-error">{error}</p>}
        {notice && <p className="nl-notice">✓ {notice}</p>}

        <div className="adm-settings-section">
          {list.length === 0 && <p className="adm-empty">No newsletters yet.</p>}
          {list.map(n => (
            <div key={n._id} className="nl-item">
              <div className="nl-item-main">
                <span className="nl-item-subject">{n.subject}</span>
                <span className="nl-item-meta">
                  {AUDIENCE_LABEL[n.audience] ?? n.audience}
                  {byTeacher(n) && <> · by {n.createdBy.name}</>}
                  {" · "}
                  {n.status === "sent" ? `Sent ${fmtDate(n.sentAt)}`
                    : n.status === "submitted" ? `Submitted ${fmtDate(n.submittedAt)}`
                    : `Updated ${fmtDate(n.updatedAt)}`}
                </span>
                {n.status === "draft" && n.reviewNote && <span className="nl-review-note">Returned: {n.reviewNote}</span>}
                {n.status !== "sent" && n.sendError && <span className="adm-error">Last send failed: {n.sendError}</span>}
                {n.stats && (
                  <span className="nl-item-stats">
                    {n.stats.delivered} delivered · {n.stats.opens} opened · {n.stats.clicks} clicked
                    {n.stats.unsubscribes ? ` · ${n.stats.unsubscribes} unsubscribed` : ""}
                    {n.stats.bounces ? ` · ${n.stats.bounces} bounced` : ""}
                  </span>
                )}
              </div>
              <span className={`adm-status-badge status-${n.status}`}>{STATUS_LABEL[n.status] ?? n.status}</span>
              <div className="nl-item-actions">
                {n.status === "draft" && <button className="adm-btn-ghost-sm" onClick={() => open(n)}>Edit</button>}
                {n.status === "submitted" && IS_OWNER  && <button className="adm-btn-ghost-sm" onClick={() => open(n)}>Review</button>}
                {n.status === "submitted" && !IS_OWNER && <button className="adm-btn-ghost-sm" onClick={() => withdraw(n)}>Withdraw</button>}
                {n.status === "sent" && <button className="adm-btn-ghost-sm" onClick={() => refreshStats(n)}>{n.stats ? "Refresh stats" : "Stats"}</button>}
                <button className="adm-btn-ghost-sm" onClick={() => duplicate(n)}>Duplicate</button>
                {(n.status === "draft" || (n.status === "submitted" && IS_OWNER)) &&
                  <button className="adm-btn-ghost-sm" onClick={() => remove(n)}>Delete</button>}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ═══ Editor ════════════════════════════════════════════
  const reviewing = IS_OWNER && form.status === "submitted";

  return (
    <div className="adm-tab-content nl-tab">
      <div className="nl-head">
        <button className="adm-btn-ghost-sm" onClick={close}>← All newsletters</button>
        <h2 className="nl-title">
          {reviewing ? `Review newsletter${form.createdBy?.name ? ` from ${form.createdBy.name}` : ""}`
            : form._id ? "Edit newsletter" : "New newsletter"}
          {dirty && <span className="nl-dirty"> · unsaved</span>}
        </h2>
      </div>
      {!IS_OWNER && form.reviewNote && <p className="nl-waiting">Returned with a note: {form.reviewNote}</p>}

      <div className="nl-editor">
        <div className="nl-col-edit">
          <div className="adm-settings-section">
            <h3 className="adm-settings-heading">Details</h3>
            <label className="adm-form-label">Subject
              <input className="adm-input" value={form.subject} placeholder="e.g. October at the studio"
                onChange={e => edit({ subject: e.target.value })} />
            </label>
            <label className="adm-form-label">Inbox preview line (optional)
              <input className="adm-input" value={form.previewText} placeholder="Shown after the subject in most inboxes"
                onChange={e => edit({ previewText: e.target.value })} />
            </label>
            <div className="adm-form-label">Send to
              <Segmented value={form.audience} onChange={v => edit({ audience: v })}
                options={audiences.map(a => ({ value: a.value, label: `${a.label}${lists ? ` (${lists[a.value]?.subscribers ?? 0})` : ""}` }))} />
            </div>
            <div className="nl-row">
              <label className="adm-form-label">Banner title (optional)
                <input className="adm-input" value={form.headerTitle} placeholder="Studio name"
                  onChange={e => edit({ headerTitle: e.target.value })} />
              </label>
              <label className="adm-form-label nl-color">Colour
                <input type="color" value={form.headerColor || defaultColor}
                  onChange={e => edit({ headerColor: e.target.value })} />
              </label>
            </div>
            <label className="nl-check">
              <input type="checkbox" checked={form.greeting} onChange={e => edit({ greeting: e.target.checked })} />
              Start with "Hi [first name],"
            </label>
            <label className="adm-form-label">Signature
              <SignatureSelect value={form.signatureId} onChange={v => edit({ signatureId: v })} />
            </label>
          </div>

          {form.blocks.map((b, i) => (
            <div key={b.id} className="nl-block">
              <div className="nl-block-head">
                <span className="nl-block-label">{BLOCK_LABEL[b.type]}</span>
                <span className="nl-block-tools">
                  <button type="button" className="nl-icon" title="Move up" disabled={i === 0} onClick={() => moveBlock(b.id, -1)}>↑</button>
                  <button type="button" className="nl-icon" title="Move down" disabled={i === form.blocks.length - 1} onClick={() => moveBlock(b.id, 1)}>↓</button>
                  <button type="button" className="nl-icon nl-icon-del" title="Remove" onClick={() => removeBlock(b.id)}>✕</button>
                </span>
              </div>
              <div className="nl-block-body">
                <BlockFields block={b} set={patch => setBlock(b.id, patch)} upload={upload} />
              </div>
            </div>
          ))}

          <div className="nl-add">
            <span className="nl-add-label">Add</span>
            {BLOCKS.map(t => (
              <button key={t.type} type="button" className="adm-btn-ghost-sm" onClick={() => addBlock(t.type)}>+ {t.label}</button>
            ))}
          </div>

          <div className="adm-settings-section nl-actions">
            {error  && <p className="adm-error">{error}</p>}
            {notice && <p className="nl-notice">✓ {notice}</p>}
            <div className="nl-row">
              <button className="adm-btn-ghost-sm" onClick={handleSave} disabled={!!busy}>
                {busy === "save" ? "Saving…" : reviewing ? "Save changes" : "Save draft"}
              </button>
            </div>

            {IS_OWNER ? (
              <>
                <div className="nl-row">
                  <input className="adm-input" type="email" value={testTo} placeholder="you@email.com"
                    onChange={e => setTestTo(e.target.value)} />
                  <button className="adm-btn-ghost-sm" onClick={handleTest} disabled={!!busy}>
                    {busy === "test" ? "Sending test…" : "Send test"}
                  </button>
                </div>
                <button className="adm-btn-primary nl-send" onClick={handleSend} disabled={!!busy || !form.blocks.length}>
                  {busy === "send" ? "Sending…" : `Send to ${recipientCount ?? ""} ${AUDIENCE_LABEL[form.audience]}`}
                </button>
                {reviewing && (
                  <div className="nl-row">
                    <button className="adm-btn-ghost-sm" onClick={handleReturn} disabled={!!busy}>
                      {busy === "return" ? "Returning…" : `Return to ${form.createdBy?.name || "teacher"}`}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="nl-row">
                  <button className="adm-btn-ghost-sm" onClick={handleTest} disabled={!!busy}>
                    {busy === "test" ? "Sending test…" : "Send test to my email"}
                  </button>
                </div>
                <button className="adm-btn-primary nl-send" onClick={handleSubmit} disabled={!!busy || !form.blocks.length}>
                  {busy === "submit" ? "Submitting…" : "Submit for approval"}
                </button>
                <p className="adm-hint">The studio owner reviews and sends it.</p>
              </>
            )}
            {listsLine}
          </div>
        </div>

        <div className="nl-col-preview">
          <div className="nl-preview-bar">
            <span className="nl-block-label">Preview</span>
            <Segmented value={device} onChange={setDevice}
              options={[{ value: "desktop", label: "Desktop" }, { value: "mobile", label: "Phone" }]} />
          </div>
          <div className="nl-preview-frame-wrap">
            <iframe title="Newsletter preview" className={`nl-preview-frame ${device}`} srcDoc={previewHtml} sandbox="" />
          </div>
        </div>
      </div>
    </div>
  );
}
